// autoLink.js - Auto-link first mention of Financlopedia terms in HTML content
import { financlopediaEntries } from '../data/financlopediaData';

/**
 * Scans HTML content and wraps the first mention of each Financlopedia term
 * with an internal link to its entry page. Skips the current entry's own term.
 *
 * @param {string} html - HTML string to process
 * @param {string} currentSlug - Slug of the current entry (to skip self-linking)
 * @param {string} lng - Current locale (es/en/fr) for locale-prefixed links
 * @returns {string} HTML with first mentions linked
 */
export function autoLinkTerms(html, currentSlug = '', lng = 'es') {
  if (!html) return html;

  // Build lookup: term variants → slug (exclude current entry)
  const termMap = [];
  financlopediaEntries.forEach((entry) => {
    if (entry.slug === currentSlug) return;
    // Add both term and termFull as possible matches
    const variants = [entry.term];
    if (entry.termFull && entry.termFull !== entry.term) {
      variants.push(entry.termFull);
    }
    variants.forEach((variant) => {
      termMap.push({ text: variant, slug: entry.slug });
    });
  });

  // Sort by length descending so longer terms match first (e.g., "Credit Score" before "Credit")
  termMap.sort((a, b) => b.text.length - a.text.length);

  const linked = new Set();
  let result = html;

  termMap.forEach(({ text, slug }) => {
    if (linked.has(slug)) return;

    // Match whole word, case-insensitive, not inside HTML tags or existing links
    // Use a regex that avoids matching inside <a> tags or HTML attributes
    const escaped = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(
      `(?<![\\w/])(?!<[^>]*>)(${escaped})(?![\\w])(?![^<]*<\\/a>)`,
      'i'
    );

    // Only replace if not already inside an <a> tag
    // Simple approach: split by <a...>...</a> blocks, only replace in non-link parts
    const parts = result.split(/(<a\b[^>]*>.*?<\/a>)/gi);
    let replaced = false;

    const newParts = parts.map((part) => {
      if (replaced || /<a\b/i.test(part)) return part;
      if (regex.test(part)) {
        replaced = true;
        return part.replace(
          regex,
          `<a href="/${lng}/financlopedia/${slug}" style="color:#1E3A5F;text-decoration:underline;font-weight:500">$1</a>`
        );
      }
      return part;
    });

    if (replaced) {
      result = newParts.join('');
      linked.add(slug);
    }
  });

  // Extra keyword linking for comparators/tools
  const EXTRA_LINK_TARGETS = [
    { keywords: ['calculadora hipotecaria', 'calculadora de hipoteca'], path: '/calculadora-hipoteca' },
    { keywords: ['comparar tarjetas', 'comparador de tarjetas'], path: '/tarjetas-de-credito' },
    { keywords: ['comparar cuentas', 'comparador de cuentas'], path: '/comparar-cuentas' },
    { keywords: ['budget planner', 'planificador de presupuesto'], path: '/budget-planner' },
  ];

  EXTRA_LINK_TARGETS.forEach(({ keywords, path }) => {
    keywords.forEach((kw) => {
      const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(?<![\\w/])(${escaped})(?![\\w])`, 'i');
      const parts = result.split(/(<a\b[^>]*>.*?<\/a>)/gi);
      let replaced = false;
      const newParts = parts.map((part) => {
        if (replaced || /<a\b/i.test(part)) return part;
        if (regex.test(part)) {
          replaced = true;
          return part.replace(
            regex,
            `<a href="/${lng}${path}" style="color:#1E3A5F;text-decoration:underline;font-weight:500">$1</a>`
          );
        }
        return part;
      });
      if (replaced) {
        result = newParts.join('');
      }
    });
  });

  return result;
}
